import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { InterpolatableTranslation, TranslateService, InterpolationParameters, Translation } from '@ngx-translate/core';
import { LangChangeEvent } from '@ngx-translate/core';
import { Observable, Subject, switchMap } from 'rxjs';
import { DateAdapter } from '@angular/material/core';

import { getTranslation } from '../helper/translation';

export const DANISH = 'da';
export const DANISH_ID = 1030;
export const DANISH_CULTURE = 'da-DK';
export const ENGLISH = 'en';
export const ENGLISH_ID = 2057;
export const ENGLISH_CULTURE = 'en-GB';
export const FINNISH = 'fi';
export const FINNISH_ID = 1035;
export const FINNISH_CULTURE = 'fi-FI';
export const SWEDISH = 'sv';
export const SWEDISH_ID = 1053;
export const SWEDISH_CULTURE = 'sv-SE';

type Translatable = {
  message?: string;
  params?: TranslationParameters;
  toString(): string;
};

export type TranslationParameters = Record<string, Translatable>;

@Injectable()
export class LanguageService {
  constructor(
    private readonly _adapter: DateAdapter<Date>,
    private readonly _translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    _translateService.onLangChange.subscribe((e: LangChangeEvent) => {
      LanguageService._currentLanguage = e.lang;
      this._adapter.setLocale(e.lang);
      this._langChangeSubject.next(e);
    });
    LanguageService._currentLanguage = this.currentLanguage;
  }

  private static _currentLanguage: string;
  private readonly _langChangeSubject = new Subject<LangChangeEvent>();

  get onLangChange(): Observable<LangChangeEvent> {
    return this._langChangeSubject.asObservable();
  }

  get currentLanguage(): string {
    return LanguageService._currentLanguage ?? this._translateService.getCurrentLang() ?? SWEDISH;
  }

  /** Load the active language before application content is first rendered. */
  initialize(): Observable<InterpolatableTranslation> {
    const language = this.getInitialLanguage();
    this.document.documentElement.lang = language === DANISH ? 'dk' : language;

    return this._translateService.setFallbackLang(SWEDISH).pipe(switchMap(() => this._translateService.use(language)));
  }

  getCurrentTranslations(): InterpolatableTranslation {
    return this._translateService.getTranslations(this.currentLanguage);
  }

  instant(key: string | string[], interpolateParams?: InterpolationParameters): Translation {
    return this._translateService.instant(key, interpolateParams);
  }

  translate(key: string | string[], interpolateParams?: InterpolationParameters): Observable<Translation> {
    return this._translateService.get(key, interpolateParams);
  }

  getCultureLanguageName(cultureName: string) {
    return this.getLanguageName(cultureName.substring(0, 2));
  }

  getLanguageName(language: string): string {
    switch (language) {
      case DANISH:
        return 'DANISH_TEXT';

      case ENGLISH:
        return 'ENGLISH_TEXT';

      case FINNISH:
        return 'FINNISH_TEXT';

      case SWEDISH:
        return 'SWEDISH_TEXT';
    }
  }

  getSupportedLanguages(): { language: string; culture: string; id: number; name: string }[] {
    const languages = [SWEDISH, ENGLISH, DANISH, FINNISH].map(l => {
      return {
        language: l,
        culture: this.getCulture(l),
        id: this.getLanguageId(l),
        name: this.getLanguageName(l),
      };
    });
    return languages;
  }

  get currentCultureLang(): string {
    return this.getCulture(this.currentLanguage);
  }

  getCulture(language: string): string {
    return LanguageService._getCulture(language);
  }

  private static _getCulture(language: string): string {
    switch (language) {
      case DANISH:
        return DANISH_CULTURE;

      case ENGLISH:
        return ENGLISH_CULTURE;

      case FINNISH:
        return FINNISH_CULTURE;

      default:
        return SWEDISH_CULTURE;
    }
  }

  get currentLanguageId(): number {
    return this.getLanguageId(this.currentLanguage);
  }

  static getCultureLang(): string {
    return LanguageService._getCulture(LanguageService._currentLanguage ?? SWEDISH);
  }

  getLanguageId(language: string): number {
    switch (language) {
      case DANISH:
        return DANISH_ID;

      case ENGLISH:
        return ENGLISH_ID;

      case FINNISH:
        return FINNISH_ID;

      case SWEDISH:
        return SWEDISH_ID;

      default:
        throw new Error(`The id is missing for language ${language}.`);
    }
  }

  private getParameterizedText(text: string, parameters: TranslationParameters): string {
    if (!text) {
      return '';
    }

    const template = this.getText(text) || text;
    let result = template;

    if (!parameters) {
      return result;
    }

    const tags = template.match(/{[^}\s]+}+/g);

    if (!tags) {
      return result;
    }

    tags.forEach(element => {
      const key = element.replace(/[{}]+/g, '');
      const parameter = parameters[key];
      let parameterValue: string;
      if (parameter === null || parameter === undefined) {
        parameterValue = '';
      } else if (typeof parameter === 'string') {
        parameterValue = this.getParameterizedText(parameter, parameters);
      } else if (parameter.message) {
        parameterValue = this.getParameterizedText(parameter.message, parameter.params);
      } else {
        parameterValue = parameter.toString();
      }
      result = result.replace(element, parameterValue);
    });
    return result;
  }

  // If the parameters parameter is provided, the first text can be (for example) '{tagOne}: {tagTwo}' - or a textId that refers to a text like that.
  // In this case, the 'tagOne'-property of the parameters object is inserted in place of the {tagOne}-placholder. If the property is missing, the placeholder will be removed.
  // If a referred property (ie "parameter.tagTwo") on the parameters object is a translateable string (normal i18n string or an object with the properties 'message' and optionally 'params'), it will be translated.
  getText(textId: string, parameters?: TranslationParameters): string {
    if (!textId) return '';

    if (parameters) return this.getParameterizedText(textId, parameters);

    let translations = this.getCurrentTranslations();
    return getTranslation(translations, textId);
  }

  setLanguage(id: number): void {
    switch (id) {
      case DANISH_ID:
        this.setDanish().subscribe();
        this.document.documentElement.lang = 'dk';
        break;

      case ENGLISH_ID:
        this.setEnglish().subscribe();
        this.document.documentElement.lang = 'en';
        break;

      case FINNISH_ID:
        this.setFinish().subscribe();
        this.document.documentElement.lang = 'fi';
        break;

      case SWEDISH_ID:
        this.setSwedish().subscribe();
        this.document.documentElement.lang = 'sv';
        break;

      default:
        throw new Error(`The language is missing for id ${id}.`);
    }
  }

  private getInitialLanguage(): string {
    const language = localStorage.getItem('lang');
    return [DANISH, ENGLISH, FINNISH, SWEDISH].includes(language) ? language : SWEDISH;
  }

  setDanish(): Observable<InterpolatableTranslation> {
    localStorage.setItem('lang', DANISH);
    return this._translateService.use(DANISH);
  }

  setEnglish(): Observable<InterpolatableTranslation> {
    localStorage.setItem('lang', ENGLISH);
    return this._translateService.use(ENGLISH);
  }

  setFinish(): Observable<InterpolatableTranslation> {
    localStorage.setItem('lang', FINNISH);
    return this._translateService.use(FINNISH);
  }

  setSwedish(): Observable<InterpolatableTranslation> {
    localStorage.setItem('lang', SWEDISH);
    return this._translateService.use(SWEDISH);
  }
}
